import { action, observable, toJS, reaction } from 'mobx';
import { env } from 'next-runtime-env';
import SocketClient from '@utils/socket';
import BaseStore from './baseStore';
import { getLobbyEvent } from '@constant/game';
import SystemStore from './system';
import UserStore from './user';
import { NUMBER_OF_ROWS } from '@components/home/LiveTable';

// * please refer to SocketStore for more details
const REDIRECT_TIMEOUT_MS = 6000;
const NEXT_PUBLIC_WS_URL = env('NEXT_PUBLIC_WS_URL')

interface LobbySocketStore {
  client: SocketClient;
}

interface PlayerStatistics {
  gameType: string;
  multiplier: number;
  payout: number;
  playerDisplayName: string;
  playerIcon: string | null;
  playerId: number;
  timestamp: number;
  wager: number;
}

class LobbySocketStore extends BaseStore {
  system: SystemStore;

  user: UserStore;

  @observable client: SocketClient;

  @observable liveTableInView: boolean = false

  @observable liveTableData: PlayerStatistics[] = [];

  @observable onlinePlayerCount: number = 0;

  @observable playerStatisticsList: PlayerStatistics[] = [];

  @action setOnlinePlayerCount(count: number) {
    if (this.onlinePlayerCount !== count){
      this.onlinePlayerCount = count;
    }
  }

  @action setPlayerStatisticList(array: PlayerStatistics[]) {
    this.playerStatisticsList = array;
    if (this.liveTableInView) {
      this.liveTableData = this.playerStatisticsList
    }
  }

  constructor(system: SystemStore, user: UserStore) {
    super();
    this.system = system;
    this.user = user;
    this.isReady = true;

    // Set up reaction to reconnect when player token changes
    reaction(
      () => this.user?.playerToken,
      (playerToken) => {
        // Only reconnect if the client is already initialized
        if (this.client) {
          console.log('Player token changed, reconnecting lobby socket...');
          this.connect();
        }
      }
    );
  
    // Wait for localStorage to update when accessing login-with-token
    // setTimeout(() => this.init(), 2000);
  }

  @action init() {
    // Get player token from localStorage if user is logged in, otherwise pass empty string
    const playerToken = this.user?.getPlayerToken() || '';
    
    this.client = new SocketClient(`${NEXT_PUBLIC_WS_URL}/lobby`, playerToken);
    
    const LOBBY_EVENTS = getLobbyEvent();

    this.client.socket.on(LOBBY_EVENTS.ONLINE_PLAYER_COUNTS, data => {
      // console.log(' lobby events - online player counts - data ', data);
      this.setOnlinePlayerCount(data);
    });
    this.client.socket.on(LOBBY_EVENTS.REAL_TIME_PLAYER_STATISTICS, data => {
      // console.log(' @@mobx - real time player stats ', data);
      this.setPlayerStatisticList(
        [
          ...data, // add the latest data at the beginning
          ...this.playerStatisticsList,
        ].slice(0, NUMBER_OF_ROWS) // keep only the latest X items
      );
    });
    this.client.socket.on(LOBBY_EVENTS.REAL_TIME_PLAYER_RANKING, data => {
      // console.log(' lobby events - player ranking - data ', data);
    });
    this.client.socket.on(LOBBY_EVENTS.REAL_TIME_ROOM_STATISTICS, data => {
      console.log(' lobby events - room stat - data ', data);
    });
  }

  @action getPlayerStatistics(): PlayerStatistics[] {
    return this.playerStatisticsList;
  }

  @action async call(...props: Parameters<typeof SocketClient.call>) {
    return this.client?.call(...props);
  }

  @action async disconnect() {
    return this.client?.disconnect();
  }

  @action async connect() {
    await this.disconnect();
    this.init();

    return this.client?.connect();
  }
}

export default LobbySocketStore;
